import { Injectable } from '@angular/core';
import { AuthenticationService, ERoleName, UserLoginRequestDto, UserResponseDto, UserService } from '@core/api';
import { BehaviorSubject, firstValueFrom, map, switchMap } from 'rxjs';
import { LocalStorageService } from '../storage/local-storage.service';
import { Router } from '@angular/router';

const TOKEN_STRING = 'TOKEN';

@Injectable({
  providedIn: 'root'
})
export class UserStateService {
  private tokenSubject: BehaviorSubject<string | null | undefined>;
  private userSubject: BehaviorSubject<UserResponseDto | null>;

  constructor(
    private authService: AuthenticationService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private userService: UserService
  ) {
    this.tokenSubject = new BehaviorSubject<string | null | undefined>(null);
    this.userSubject = new BehaviorSubject<UserResponseDto | null>(null);
  }

  get token(): BehaviorSubject<string | null | undefined> {
    return this.tokenSubject;
  }

  get user(): BehaviorSubject<UserResponseDto | null> {
    return this.userSubject;
  }

  login(userLoginRequestDto: UserLoginRequestDto) {
    return this.authService.v1AuthenticationLoginPost(userLoginRequestDto).pipe(
      map(r => {
        const jwtToken: string | null = r.jwtToken ? r.jwtToken : null;
        if (jwtToken) {
          this.localStorageService.setItem(TOKEN_STRING, jwtToken);
        }
        this.token.next(r.jwtToken);
        return r;
      }),
      switchMap(() =>
        this.userService.v1UserMeGet().pipe(
          map(user => {
            this.user.next(user);
            return user;
          })
        )
      )
    );
  }

  logout() {
    this.localStorageService.removeItem(TOKEN_STRING);
    this.tokenSubject.next(null);
    this.userSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  async init() {
    const token = this.localStorageService.getItem(TOKEN_STRING);
    if (token) {
      this.tokenSubject.next(token);
      try {
        const user = await firstValueFrom(this.userService.v1UserMeGet());
        this.user.next(user);
      } catch (error) {
        console.error('Error fetching user data', error);
      }
    }
  }

  isAuthenticated(): boolean {
    if (this.token.value) return true;
    else return false;
  }

  getTenantId(): string | null | undefined {
    return this.userSubject.value?.tenantId;
  }

  getUserState(): UserResponseDto | null {
    return this.user.value;
  }

  isSuperAdmin(): boolean {
    if (this.user.value?.roleName && this.user.value?.roleName === ERoleName.SuperAdmin) return true;
    else return false;
  }
}
